import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Translate as TranslateIcon,
  ExpandMore as ExpandMoreIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { productCategoriesApi } from '../services/api';
import { imageApi } from '../services/imageApi';

const CategoryForm = ({ open, onClose, onSubmit, editingCategory, categories = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
    parent_category_id: '',
    image_url: ''
  });
  const [translations, setTranslations] = useState({}); // { locale: { name, description } }
  const [availableLocales] = useState([
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'he', name: 'עברית', flag: '🇮🇱' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ]);
  const [selectedLocale, setSelectedLocale] = useState('en');
  const [showTranslations, setShowTranslations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Get available parent categories (excluding current category and its descendants when editing)
  const getAvailableParentCategories = () => {
    if (!editingCategory) {
      return categories.filter(cat => cat.active);
    }

    // When editing, exclude the current category and its descendants
    const getDescendantIds = (categoryId) => {
      const descendants = new Set([categoryId]);
      const findDescendants = (parentId) => {
        categories
          .filter(cat => cat.parent_category_id === parentId)
          .forEach(child => {
            descendants.add(child.id);
            findDescendants(child.id);
          });
      };
      findDescendants(categoryId);
      return descendants;
    };

    const excludedIds = getDescendantIds(editingCategory.id);
    return categories.filter(cat => cat.active && !excludedIds.has(cat.id));
  };

  const availableParentCategories = getAvailableParentCategories();

  // Initialize form data when editing category changes
  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name || '',
        description: editingCategory.description || '',
        active: editingCategory.active ?? true,
        parent_category_id: editingCategory.parent_category_id || '',
        image_url: editingCategory.image_url || ''
      });
      // Set image preview if category has an image
      if (editingCategory.image_url) {
        setImagePreview(editingCategory.image_url);
      } else {
        setImagePreview(null);
      }
      // Load existing translations if available
      if (editingCategory.translations) {
        setTranslations(editingCategory.translations);
      } else {
        setTranslations({});
      }
    } else {
      setFormData({
        name: '',
        description: '',
        active: true,
        parent_category_id: '',
        image_url: ''
      });
      setImagePreview(null);
      setTranslations({});
    }
    setError(null);
    setImageFile(null);
    setSelectedLocale('en');
    setShowTranslations(false);
  }, [editingCategory, open]);

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image file selection
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file must be less than 5MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle image removal
  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_url: ''
    }));
  };

  // Handle translation changes
  const handleTranslationChange = (locale, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [locale]: {
        ...prev[locale],
        [field]: value
      }
    }));
  };

  // Add a new translation for a locale
  const addTranslation = (locale) => {
    if (!translations[locale]) {
      setTranslations(prev => ({
        ...prev,
        [locale]: {
          name: '',
          description: ''
        }
      }));
      setSelectedLocale(locale);
    }
  };

  // Remove translation for a locale
  const removeTranslation = (locale) => {
    if (locale !== 'en') { // Don't allow removing English
      setTranslations(prev => {
        const newTranslations = { ...prev };
        delete newTranslations[locale];
        return newTranslations;
      });
      if (selectedLocale === locale) {
        setSelectedLocale('en');
      }
    }
  };

  // Get translation for current locale or fallback
  const getCurrentTranslation = (locale) => {
    return translations[locale] || { name: '', description: '' };
  };

  // Upload image and return the image URL
  const uploadImageIfNeeded = async () => {
    if (!imageFile) return null;
    
    setUploadingImage(true);
    try {
      const response = await imageApi.uploadImage(imageFile);
      if (response && response.mediumUrl) {
        return response.mediumUrl; // Use medium size for category images
      }
      throw new Error('Failed to get image URL from upload response');
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error(`Image upload failed: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Upload image first if there's a new image file
      let imageUrl = formData.image_url; // Keep existing URL if no new image
      if (imageFile) {
        imageUrl = await uploadImageIfNeeded();
      }

      // Filter and validate translations
      const validTranslations = {};
      Object.keys(translations).forEach(locale => {
        const translation = translations[locale];
        if (translation && translation.name && translation.name.trim()) {
          // Only include translations with valid names
          validTranslations[locale] = {
            name: translation.name.trim(),
            description: (translation.description || '').trim() || null
          };
        }
      });

      // Prepare data for API
      const apiData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        active: formData.active,
        parent_category_id: formData.parent_category_id || null,
        image_url: imageUrl || null,
        translations: Object.keys(validTranslations).length > 0 ? validTranslations : null
      };

      // Validate required fields
      if (!apiData.name) {
        throw new Error('Category name is required');
      }

      if (apiData.name.length > 100) {
        throw new Error('Category name must not exceed 100 characters');
      }

      if (apiData.description && apiData.description.length > 500) {
        throw new Error('Description must not exceed 500 characters');
      }

      // Validate translations
      Object.keys(validTranslations).forEach(locale => {
        const translation = validTranslations[locale];
        if (translation.name.length > 100) {
          throw new Error(`Translation name for ${locale.toUpperCase()} must not exceed 100 characters`);
        }
        if (translation.description && translation.description.length > 500) {
          throw new Error(`Translation description for ${locale.toUpperCase()} must not exceed 500 characters`);
        }
      });

      // Make API call
      if (editingCategory) {
        await productCategoriesApi.updateCategory(editingCategory.id, apiData);
      } else {
        await productCategoriesApi.createCategory(apiData);
      }

      // Success - notify parent component
      onSubmit();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Get parent category hierarchy for display
  const getParentHierarchy = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return '';

    const hierarchy = [];
    let current = category;
    while (current) {
      hierarchy.unshift(current.name);
      current = categories.find(cat => cat.id === current.parent_category_id);
    }
    return hierarchy.join(' > ');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Create New Category'}
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Category Name */}
          <TextField
            autoFocus
            required
            fullWidth
            label="Category Name"
            value={formData.name}
            onChange={handleChange('name')}
            margin="normal"
            disabled={loading}
            inputProps={{ maxLength: 100 }}
            helperText={`${formData.name.length}/100 characters`}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
            inputProps={{ maxLength: 500 }}
            helperText={`${formData.description.length}/500 characters`}
          />

          {/* Parent Category */}
          <FormControl fullWidth margin="normal" disabled={loading}>
            <InputLabel>Parent Category (Optional)</InputLabel>
            <Select
              value={formData.parent_category_id}
              onChange={handleChange('parent_category_id')}
              label="Parent Category (Optional)"
            >
              <MenuItem value="">
                <em>No parent (Root category)</em>
              </MenuItem>
              {availableParentCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Box>
                    <Typography variant="body2">
                      {getParentHierarchy(category.id) || category.name}
                    </Typography>
                    {category.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {category.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Active Status */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={handleChange('active')}
                disabled={loading}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />

          {/* Category Image */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Category Image
            </Typography>
            
            {/* Image Preview */}
            {imagePreview && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <img
                  src={imagePreview}
                  alt="Category preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #ddd'
                  }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleImageRemove}
                  disabled={loading || uploadingImage}
                >
                  Remove Image
                </Button>
              </Box>
            )}
            
            {/* Image Upload */}
            <Button
              variant="outlined"
              component="label"
              disabled={loading || uploadingImage}
              sx={{ mb: 1 }}
            >
              {imagePreview ? 'Change Image' : 'Upload Image'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>
            
            <Typography variant="caption" display="block" color="text.secondary">
              Recommended: Square image, at least 300x300 pixels, max 5MB
            </Typography>
          </Box>

          {/* Localization Section */}
          <Box sx={{ mt: 3 }}>
            <Accordion 
              expanded={showTranslations} 
              onChange={(event, isExpanded) => setShowTranslations(isExpanded)}
              sx={{ border: '1px solid #e0e0e0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon color="primary" />
                  <Typography variant="h6">
                    Translations ({Object.keys(translations).length} languages)
                  </Typography>
                  {Object.keys(translations).length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {Object.keys(translations).slice(0, 4).map(locale => (
                        <Chip 
                          key={locale}
                          label={locale.toUpperCase()}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                      {Object.keys(translations).length > 4 && (
                        <Chip 
                          label={`+${Object.keys(translations).length - 4}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Add Translation Controls */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Add Translation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {availableLocales
                      .filter(locale => !translations[locale.code])
                      .map(locale => (
                        <Button
                          key={locale.code}
                          size="small"
                          variant="outlined"
                          onClick={() => addTranslation(locale.code)}
                          startIcon={<span style={{ fontSize: '16px' }}>{locale.flag}</span>}
                          disabled={loading}
                        >
                          {locale.name}
                        </Button>
                      ))}
                  </Box>
                </Box>

                {/* Existing Translations */}
                {Object.keys(translations).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Manage Translations
                    </Typography>
                    
                    {/* Translation Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs
                        value={selectedLocale}
                        onChange={(e, newValue) => setSelectedLocale(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        {Object.keys(translations).map(locale => {
                          const localeInfo = availableLocales.find(l => l.code === locale) || 
                            { name: locale.toUpperCase(), flag: '🌐' };
                          return (
                            <Tab
                              key={locale}
                              value={locale}
                              icon={<span style={{ fontSize: '16px' }}>{localeInfo.flag}</span>}
                              label={localeInfo.name}
                              iconPosition="start"
                            />
                          );
                        })}
                      </Tabs>
                    </Box>

                    {/* Translation Form for Selected Language */}
                    {Object.keys(translations).map(locale => (
                      <Box
                        key={locale}
                        sx={{ 
                          display: locale === selectedLocale ? 'block' : 'none',
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: 'grey.50'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">
                            {availableLocales.find(l => l.code === locale)?.flag} {' '}
                            {availableLocales.find(l => l.code === locale)?.name || locale.toUpperCase()}
                          </Typography>
                          {locale !== 'en' && (
                            <Tooltip title="Remove Translation">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeTranslation(locale)}
                                disabled={loading}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <TextField
                          fullWidth
                          label={`Category Name (${locale.toUpperCase()})`}
                          value={getCurrentTranslation(locale).name || ''}
                          onChange={(e) => handleTranslationChange(locale, 'name', e.target.value)}
                          margin="normal"
                          disabled={loading}
                          inputProps={{ maxLength: 100 }}
                          helperText={`${getCurrentTranslation(locale).name?.length || 0}/100 characters`}
                          required
                        />

                        <TextField
                          fullWidth
                          label={`Description (${locale.toUpperCase()})`}
                          value={getCurrentTranslation(locale).description || ''}
                          onChange={(e) => handleTranslationChange(locale, 'description', e.target.value)}
                          margin="normal"
                          multiline
                          rows={3}
                          disabled={loading}
                          inputProps={{ maxLength: 500 }}
                          helperText={`${getCurrentTranslation(locale).description?.length || 0}/500 characters`}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {Object.keys(translations).length === 0 && (
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <TranslateIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No translations added yet. Add translations to support multiple languages.
                    </Typography>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Preview */}
          {formData.name && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview:
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {formData.name}
              </Typography>
              {formData.description && (
                <Typography variant="body2">
                  <strong>Description:</strong> {formData.description}
                </Typography>
              )}
              {formData.parent_category_id && (
                <Typography variant="body2">
                  <strong>Parent:</strong> {getParentHierarchy(parseInt(formData.parent_category_id))}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Status:</strong> {formData.active ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || uploadingImage || !formData.name.trim()}
          >
            {loading ? 'Saving...' : 
             uploadingImage ? 'Uploading Image...' : 
             (editingCategory ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryForm;